/**
 * Open flags as one table (15 October 2026).
 *
 * The page was a section per worker with that worker's flags beneath, grouped again by kind. At
 * twenty-odd flags a day the owner found it hard to track, and asked for a table — the same shape
 * as the Attendance day. One row per flag: who, what, when, the detail, and the two things to do
 * with it. Tabs by kind carry the counts and filter; a name filter narrows to one person.
 *
 * Resolving: tick rows and resolve them together with one optional note, or press Resolve on a
 * row for just that one. Either way the same bar asks for the note, so there is one place it is
 * written. Resolving records that somebody has seen it — it never changes a punch or a day.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CircleCheck, Search } from 'lucide-react';
import { api, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate } from '../../lib/ui.js';
import { Avatar, EmptyState, Skeleton, Table, type Tone } from '../../lib/components.js';
import { usePhotos } from '../photos.js';
import { KIND, type Flag } from '../flag-kinds.js';
import { t } from '../../i18n/index.js';

const kindOf = (kind: string) =>
  KIND[kind] ?? { label: kind, icon: AlertTriangle, tone: 'neutral' as Tone, means: '' };

export function FlagsPage() {
  const { photoOf } = usePhotos();
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** The resolve bar is open (for the ticked rows), and the note being written. */
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api<{ flags: Flag[] }>('/admin/flags?status=open');
      setFlags(r.flags);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const all = useMemo(
    () => [...(flags ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [flags],
  );
  // Kinds present, most frequent first: the tabs.
  const kinds = useMemo(() => {
    const n = new Map<string, number>();
    for (const f of all) n.set(f.kind, (n.get(f.kind) ?? 0) + 1);
    return [...n.entries()].sort((a, b) => b[1] - a[1]);
  }, [all]);
  const q = query.trim().toLowerCase();
  const shown = all.filter(
    (f) =>
      (tab === 'all' || f.kind === tab) &&
      (!q || (f.worker?.name ?? t('flags_system')).toLowerCase().includes(q)),
  );
  const pickedShown = shown.filter((f) => picked.has(f.id));
  const allShownPicked = shown.length > 0 && pickedShown.length === shown.length;

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const resolve = async () => {
    const ids = [...picked];
    if (ids.length === 0) return;
    setBusy(true);
    try {
      for (const id of ids) await post(`/admin/flags/${id}/resolve`, { note: note || undefined });
      setPicked(new Set());
      setResolving(false);
      setNote('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      {error && <Alert>{error}</Alert>}
      <section className="card day-table-card flags-card" id="flags">
        <div className="flags-head">
          <div>
            <h2 className="card-title">
              {t('flags_title')} ({flags?.length ?? 0})
            </h2>
            <p className="muted card-sub">{t('flags_sub')}</p>
          </div>
        </div>
        <div className="day-table-head">
          <div className="day-tabs" role="tablist" aria-label={t('day_filter')}>
            {[['all', all.length] as [string, number], ...kinds].map(([k, n]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`day-tab${tab === k ? ' active' : ''}`}
                onClick={() => setTab(k)}
              >
                {k === 'all' ? t('day_tab_all') : kindOf(k).label}{' '}
                <span className="day-tab-n">{n}</span>
              </button>
            ))}
          </div>
          <span className="search table-search" role="search">
            <Search size={15} className="search-icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('day_filter_name')}
              aria-label={t('day_filter_name')}
            />
          </span>
        </div>

        {/* One bar for resolving, whether one row or many: it is where the note is written. */}
        {picked.size > 0 && (
          <div className="flags-bar" role="region" aria-label={t('flags_resolve_bar')}>
            <strong>{t('flags_selected', { n: picked.size })}</strong>
            {resolving ? (
              <>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('flags_note_placeholder')}
                  maxLength={300}
                  aria-label={t('flags_note_placeholder')}
                  autoFocus
                />
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  onClick={() => void resolve()}
                >
                  {t('flags_resolve_confirm')}
                </button>
              </>
            ) : (
              <button type="button" className="small" onClick={() => setResolving(true)}>
                {t('flags_resolve_n', { n: picked.size })}
              </button>
            )}
            <button
              type="button"
              className="small secondary"
              onClick={() => {
                setPicked(new Set());
                setResolving(false);
                setNote('');
              }}
            >
              {t('cancel')}
            </button>
          </div>
        )}

        {!flags ? (
          <div className="flags-pad">
            <Skeleton lines={4} />
          </div>
        ) : all.length === 0 ? (
          <div className="flags-pad">
            <EmptyState icon={CircleCheck} text={t('flags_none')} />
          </div>
        ) : (
          <Table className="stacks att-table flags-table">
            <thead>
              <tr>
                <th scope="col" className="pick-col">
                  <input
                    type="checkbox"
                    aria-label={t('flags_pick_all')}
                    checked={allShownPicked}
                    onChange={() =>
                      setPicked((p) => {
                        const next = new Set(p);
                        for (const f of shown) {
                          if (allShownPicked) next.delete(f.id);
                          else next.add(f.id);
                        }
                        return next;
                      })
                    }
                  />
                </th>
                <th scope="col">{t('pay_worker')}</th>
                <th scope="col">{t('flags_col_flag')}</th>
                <th scope="col">{t('flags_col_when')}</th>
                <th scope="col">{t('flags_col_detail')}</th>
                <th scope="col" className="ta-right">
                  {t('worker_actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => {
                const k = kindOf(f.kind);
                const Icon = k.icon;
                return (
                  <tr key={f.id} data-flag={f.id} className={picked.has(f.id) ? 'picked' : ''}>
                    <td className="pick-col">
                      <input
                        type="checkbox"
                        aria-label={t('flags_pick', { name: f.worker?.name ?? t('flags_system') })}
                        checked={picked.has(f.id)}
                        onChange={() => toggle(f.id)}
                      />
                    </td>
                    <td className="cell-name">
                      <span className="person">
                        {f.worker ? (
                          <>
                            <Avatar
                              name={f.worker.name}
                              size={28}
                              photoUrl={photoOf(f.worker.id)}
                            />
                            <Link to={`/workers/${f.worker.id}`}>{f.worker.name}</Link>
                          </>
                        ) : (
                          t('flags_system')
                        )}
                      </span>
                    </td>
                    <td data-label={t('flags_col_flag')}>
                      {/* What the kind means, in the owner's words, is on hover rather than
                          repeated on every row. */}
                      <span className={`badge flag-kind tone-${k.tone}`} title={k.means}>
                        <Icon size={12} aria-hidden="true" /> {k.label}
                      </span>
                    </td>
                    <td data-label={t('flags_col_when')} className="nowrap">
                      {fmtDate(f.created_at)}
                    </td>
                    <td data-label={t('flags_col_detail')} className="flag-detail">
                      {f.detail}
                    </td>
                    <td className="ta-right nowrap">
                      {f.worker && (
                        <Link
                          className="button small ghost"
                          to={`/workers/${f.worker.id}?date=${f.work_date}`}
                        >
                          {t('flags_see_day')}
                        </Link>
                      )}{' '}
                      <button
                        type="button"
                        className="small secondary"
                        disabled={busy}
                        onClick={() => {
                          setPicked(new Set([f.id]));
                          setResolving(true);
                          setNote('');
                        }}
                      >
                        {t('flags_resolve')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted empty-cell">
                    {t('day_none_here')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </section>
    </div>
  );
}
